import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Home, BookOpen, Calendar, Heart, Receipt, LogOut, Menu, X, ArrowLeft } from "lucide-react";

const LOGO = "/ashra-logo.png";

const PORTAL_NAV = [
  { to: "/customer",             label: "Overview",    icon: Home,     testid: "portal-nav-overview" },
  { to: "/customer/library",     label: "Library",     icon: BookOpen, testid: "portal-nav-library" },
  { to: "/customer/experiences", label: "Experiences", icon: Calendar, testid: "portal-nav-experiences" },
  { to: "/customer/memberships", label: "Memberships", icon: Heart,    testid: "portal-nav-memberships" },
  { to: "/customer/purchases",   label: "Purchases",   icon: Receipt,  testid: "portal-nav-purchases" },
];

export default function CustomerPortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-line">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/customer" className="flex items-center gap-2">
              <img src={LOGO} alt="Ashra" className="h-7" />
              <span className="hidden sm:inline pill pill-ai">Customer</span>
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-1">
            {PORTAL_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/customer"} data-testid={n.testid}
                className={({ isActive }) => `inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition ${
                  isActive ? "bg-ai-soft text-ai" : "text-meta hover:text-ink hover:bg-zinc-50"
                }`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {user?.is_admin && (
              <Link to="/dashboard" data-testid="back-to-creator" className="text-xs text-ai font-semibold hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Creator app
              </Link>
            )}
            <div className="hidden sm:flex items-center gap-2">
              <div className="h-8 w-8 rounded-full ashra-gradient-bg grid place-items-center text-white text-xs font-bold">{user?.name?.[0]?.toUpperCase() || "C"}</div>
            </div>
            <button onClick={doLogout} className="p-2 rounded-lg hover:bg-zinc-50 text-meta" data-testid="portal-logout">
              <LogOut className="h-4 w-4" />
            </button>
            <button onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-lg border border-line" data-testid="portal-menu-toggle">
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-line bg-white">
            <div className="max-w-6xl mx-auto px-4 py-3 grid gap-1">
              {PORTAL_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/customer"} onClick={() => setOpen(false)}
                  className={({ isActive }) => `inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium ${
                    isActive ? "bg-ai-soft text-ai" : "text-ink-soft hover:bg-zinc-50"
                  }`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-10"><Outlet /></main>
    </div>
  );
}
